'use client';

import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useEffect, useId, useRef, useState } from 'react';
import { generateDirectusSrc } from '@/helpers/image';
import { CloserLookData } from '@/types/generalPage';
import GeneralPageSection from './GeneralPageSection';

const FsLightbox = dynamic(() => import('fslightbox-react'), { ssr: false });

export default function CloserLook({ data }: { data: CloserLookData }) {
  const headingId = useId();
  const images = (data.gallery_image ?? []).flatMap((item) => {
    const file = item.directus_files_id;
    return file
      ? [
          {
            src: generateDirectusSrc(file.id, 'original'),
            alt: file.description?.trim() || data.title || file.title || '',
          },
        ]
      : [];
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [viewerKey, setViewerKey] = useState(0);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    // The site's global FsLightbox styles override its built-in scroll lock.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
      triggerRef.current?.focus({ preventScroll: true });
    };
  }, [isOpen]);

  if (!images.length) return null;

  const renderTile = (index: number, className: string) => {
    const image = images[index];
    if (!image) return null;

    const remainingCount = index === 3 ? images.length - 3 : 0;

    return (
      <button
        type="button"
        aria-label={
          remainingCount > 1
            ? `Open image ${index + 1} of ${images.length} and browse the remaining ${remainingCount} gallery images`
            : `Open image ${index + 1} of ${images.length}: ${image.alt}`
        }
        onClick={(event) => {
          triggerRef.current = event.currentTarget;
          setSelectedIndex(index);
          setIsOpen(true);
        }}
        className={`relative block w-full min-w-0 overflow-hidden rounded-[10px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black ${className}`}
      >
        <Image
          src={image.src}
          alt={image.alt}
          fill
          sizes="(min-width: 1388px) 680px, (min-width: 768px) 50vw, calc(100vw - 32px)"
          className="object-cover"
        />
        {remainingCount > 1 && (
          <span
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center text-[32px] font-bold leading-10 text-[#A3A3A3]"
          >
            +{remainingCount}
          </span>
        )}
      </button>
    );
  };

  return (
    <GeneralPageSection>
      <section
        aria-labelledby={headingId}
        className="flex flex-col gap-8 md:gap-16"
      >
        <header className="flex flex-col gap-2 text-center">
          <h2
            id={headingId}
            className="text-2xl font-bold leading-8 md:text-[32px] md:leading-[48px]"
          >
            {data.title}
          </h2>
          <p className="text-base leading-7 text-[#5C5C5C] md:text-lg">
            {data.subtitle}
          </p>
        </header>

        <div
          className={`grid grid-cols-1 gap-2 md:aspect-[1340/688] md:gap-3 xl:gap-5 ${images.length > 1 ? 'md:grid-cols-[minmax(0,680fr)_minmax(0,640fr)]' : ''}`}
        >
          {renderTile(0, 'aspect-[680/688] md:aspect-auto')}
          {images.length > 1 && (
            <div
              className={`grid min-h-0 gap-2 md:gap-3 xl:gap-5 ${images.length > 2 ? 'grid-rows-2' : ''}`}
            >
              {renderTile(1, 'aspect-[640/334] md:aspect-auto')}
              {images.length > 2 && (
                <div
                  className={`grid min-h-0 gap-2 md:gap-3 xl:gap-[23px] ${images.length > 3 ? 'grid-cols-[minmax(0,278fr)_minmax(0,339fr)]' : 'grid-cols-1'}`}
                >
                  {renderTile(2, '')}
                  {renderTile(3, '')}
                </div>
              )}
            </div>
          )}
        </div>

        <FsLightbox
          key={viewerKey}
          toggler={isOpen}
          sources={images.map((image) => image.src)}
          customAttributes={images.map((image) => ({ alt: image.alt }))}
          type="image"
          slide={selectedIndex + 1}
          loadOnlyCurrentSource
          onClose={() => {
            setIsOpen(false);
            setViewerKey((key) => key + 1);
          }}
        />
      </section>
    </GeneralPageSection>
  );
}
